/**
 * Map a raw row (from CSV column mapping) to canonical Product.
 * Generates id (uuid) if missing; applies defaults for currency and category.
 */

import { v4 as uuidv4 } from "uuid";
import type { Product } from "@/types/product";

export type NormalizeError = { rowIndex: number; message: string };

export function normalizeRowToProduct(
  row: Record<string, string | number | undefined>,
  rowIndex: number
): { ok: true; product: Product } | { ok: false; error: NormalizeError } {
  const getStr = (k: string) => {
    const v = row[k];
    return v === undefined || v === null ? "" : String(v).trim();
  };
  const getNum = (k: string) => {
    const v = row[k];
    if (v === undefined || v === null) return NaN;
    // Strip currency symbols, spaces, and commas before parsing (e.g. "$10.00" → "10.00")
    const cleaned = typeof v === "number" ? v : parseFloat(String(v).replace(/[^0-9.\-]/g, ""));
    return Number.isNaN(cleaned) ? NaN : cleaned;
  };

  const title = getStr("title");
  const price = getNum("price");

  if (!title) {
    return { ok: false, error: { rowIndex, message: "Missing required field: title" } };
  }
  if (Number.isNaN(price) || price < 0) {
    return { ok: false, error: { rowIndex, message: "Invalid or missing price" } };
  }

  let id = getStr("id");
  if (!id) id = uuidv4();

  const tagsRaw = getStr("tags");
  const tags = tagsRaw
    ? tagsRaw.split(/[,;|]/).map((t) => t.trim()).filter(Boolean)
    : undefined;

  const product: Product = {
    id,
    sku: getStr("sku") || undefined,
    title,
    description: getStr("description") || undefined,
    price,
    currency: getStr("currency") || "USD",
    category: getStr("category") || "Uncategorized",
    imageUrl: getStr("imageUrl") || undefined,
    inventory: (() => {
      const inv = getNum("inventory");
      return Number.isNaN(inv) ? undefined : Math.max(0, Math.floor(inv));
    })(),
    tags: tags?.length ? tags : undefined,
    size: getStr("size") || undefined,
  };

  return { ok: true, product };
}

/**
 * Normalize an array of rows into Product[]. Returns first error if any row fails.
 */
export function normalizeRowsToProducts(
  rows: Record<string, string | number | undefined>[]
): { ok: true; products: Product[] } | { ok: false; error: NormalizeError } {
  const products: Product[] = [];
  for (let i = 0; i < rows.length; i++) {
    const result = normalizeRowToProduct(rows[i], i + 1);
    if (!result.ok) return result;
    products.push(result.product);
  }
  return { ok: true, products };
}
